<!--
  ChuteTests — THE hot path.

  Spec, Screens 5: "Chute test entry — the hot path during a pour. Add row,
  enter counts, grout weight, water volume; W/C and range verdicts appear
  immediately." Spec, Risks: "Adoption. If it is slower than the book, it will
  be abandoned. This is why pre-fill, keypads and the chute-test hot path are
  requirements, not polish."

  Design decisions this file makes, and why:

  * Adding a test POSTs immediately with the sample time and the shift person's
    initials already filled. The row exists server-side before a single number
    is typed, so nothing a QC person enters can be lost by never pressing save.

  * Rows are cards, not a table. Seventeen columns cannot be entered one-handed
    on a phone. The card header carries the values you scan for (test point,
    time, W/C, spread/flow verdicts, accepted) and the body carries entry.

  * Fields are grouped by the order a person actually works in — sample, then
    acceptance readings, then the batch numbers that produce W/C, then temps,
    then the result. The paper's column order is preserved in the CSV export,
    which is where column order matters.

  * Every arithmetic value comes from ../../lib/calc. Nothing is recomputed
    here, so the phone and the server can never disagree.

  * Out-of-range spread and flow are FLAGGED with the procedure's corrective
    instruction and never blocked — recording a bad reading is the point.

  * Mud balance is captured with no verdict (spec: no acceptance range has been
    supplied and the app must not invent one for a QA record).

  * No modal dialogs. Delete is a two-tap inline confirm that times out.
-->
<script lang="ts">
  import type {
    ChuteTest,
    ChuteTestPoint,
    CreateChuteTestRequest,
    Photo,
    SqlBool,
    Timestamp,
    UpdateChuteTestRequest
  } from '../../lib/types';
  import {
    CHUTE_TEST_POINT_LABELS,
    FLOW_MAX_S,
    FLOW_MIN_S,
    FLOW_VERDICT_LABELS,
    SPREAD_MAX_IN,
    SPREAD_MIN_IN,
    SPREAD_VERDICT_LABELS
  } from '../../lib/types';
  import { flowVerdict, spreadVerdict, wcRatio, wcRatioDisplay } from '../../lib/calc';
  import NumberField from '../../lib/fields/NumberField.svelte';
  import YesNoField from '../../lib/fields/YesNoField.svelte';
  import NotesField from '../../lib/fields/NotesField.svelte';
  import PhotoField from '../../lib/fields/PhotoField.svelte';
  import InitialsTimeField from '../../lib/fields/InitialsTimeField.svelte';
  import VerdictBadge from '../../lib/fields/VerdictBadge.svelte';
  import { clearDraft, loadDraft, saveDraft } from '../../lib/fields/local-draft';

  interface Props {
    pourId: number;
    /** Server rows, any order; rendered by seq. */
    chuteTests: ChuteTest[];
    /** Session shift name — pre-fills tester initials on a new row. */
    shiftName?: string | null;
    /**
     * The whole pour's photos. Each row's PhotoField picks out its own
     * (section 'chute_test', row_id = that test's id), so a photo of a spread
     * board or a mud balance is filed against the test it evidences.
     */
    photos?: Photo[];
    /** Client session, or a closed pour: read-only. */
    readonly?: boolean;
    /**
     * Photos alone stay editable after the pour closes — the owner wants
     * evidence addable after close. Deliberately REQUIRED, not defaulted to
     * `readonly`: a caller passing neither `readonly` nor `photosReadOnly`
     * would otherwise get an explicit `readOnly={false}` on the nested
     * PhotoField, which overrides PhotoField's own role-derived safety
     * default (`readOnly = currentRole() !== 'crew'`) — a non-crew session
     * gaining a photo control it should not have. Making this required turns
     * that failure mode into a type error instead.
     */
    photosReadOnly: boolean;
    /** The pour's `closed_at`, threaded through to each row's PhotoField. */
    closedAt?: Timestamp | null;
    /** Resolves to the created row so it can be expanded and focused. */
    onCreate?: (body: CreateChuteTestRequest) => Promise<ChuteTest | void>;
    onUpdate?: (testId: number, body: UpdateChuteTestRequest) => Promise<void>;
    onDelete?: (testId: number) => Promise<void>;
  }

  let {
    pourId,
    chuteTests,
    shiftName = null,
    photos = [],
    readonly = false,
    photosReadOnly,
    closedAt = null,
    onCreate,
    onUpdate,
    onDelete
  }: Props = $props();

  /* ------------------------------------------------------------------ draft */

  /** Editable fields. `wc_ratio` is excluded: the server computes and owns it. */
  type Editable = Omit<
    ChuteTest,
    'id' | 'pour_id' | 'seq' | 'wc_ratio' | 'created_at' | 'updated_at'
  >;
  type Draft = Partial<Editable>;

  const DRAFT_KEY = `chute.${pourId}`;

  interface DraftState {
    /** testId -> locally held values, overlaid on the server row. */
    values: Record<number, Draft>;
    /** testId -> field names not yet confirmed by the server. */
    dirty: Record<number, string[]>;
  }

  const restored = loadDraft<DraftState>(DRAFT_KEY);
  let values = $state<Record<number, Draft>>(restored?.values ?? {});
  let dirty = $state<Record<number, string[]>>(restored?.dirty ?? {});

  let saving = $state<Record<number, boolean>>({});
  let errors = $state<Record<number, string | null>>({});
  let expanded = $state<Record<number, boolean>>({});
  let confirmingDelete = $state<number | null>(null);
  let confirmTimer: ReturnType<typeof setTimeout> | null = null;
  let adding = $state(false);
  let addError = $state<string | null>(null);

  $effect(() => {
    // Persist as the user types. Cheap, and the whole point of the buffer.
    const snapshot: DraftState = { values: $state.snapshot(values), dirty: $state.snapshot(dirty) };
    if (Object.keys(snapshot.dirty).length === 0 && Object.keys(snapshot.values).length === 0) {
      clearDraft(DRAFT_KEY);
    } else {
      saveDraft(DRAFT_KEY, snapshot);
    }
  });

  let rows = $derived(
    [...chuteTests]
      .sort((a, b) => a.seq - b.seq)
      .map((base) => ({ base, view: { ...base, ...(values[base.id] ?? {}) } as ChuteTest }))
  );

  function isDirty(id: number): boolean {
    return (dirty[id]?.length ?? 0) > 0;
  }

  function set<K extends keyof Editable>(id: number, key: K, v: Editable[K]) {
    if (readonly) return;
    if (!values[id]) values[id] = {};
    values[id][key] = v;
    const keys = dirty[id] ?? [];
    if (!keys.includes(key as string)) dirty[id] = [...keys, key as string];
  }

  /**
   * Flush every pending field of one row in a single PATCH. The row is the
   * unit that gets flushed, so tabbing across a row costs one request, not one
   * per field.
   *
   * On success the local overlay is pruned only where it now matches the
   * server row; if the parent has not yet refreshed the prop, the overlay stays
   * and the screen keeps showing what was typed.
   */
  async function flush(id: number) {
    const pending = dirty[id];
    if (readonly || !pending || pending.length === 0 || saving[id]) return;

    const draft = values[id] ?? {};
    const patch: Record<string, unknown> = {};
    for (const key of pending) patch[key] = (draft as Record<string, unknown>)[key];

    saving[id] = true;
    errors[id] = null;
    try {
      await onUpdate?.(id, patch as UpdateChuteTestRequest);
      // Drop only the keys we actually sent, with the values we sent — anything
      // typed while the request was in flight stays pending.
      const still = (dirty[id] ?? []).filter(
        (k) => (values[id] as Record<string, unknown>)?.[k] !== patch[k]
      );
      if (still.length === 0) delete dirty[id];
      else dirty[id] = still;
      prune(id);
    } catch (e) {
      // Never discard the draft because a request failed.
      errors[id] = e instanceof Error ? e.message : 'Could not save. Tap retry.';
    } finally {
      saving[id] = false;
    }
  }

  /** Discard overlay values that the server row now agrees with. */
  function prune(id: number) {
    const base = chuteTests.find((t) => t.id === id);
    const draft = values[id];
    if (!base || !draft) return;
    for (const k of Object.keys(draft) as (keyof Editable)[]) {
      if (dirty[id]?.includes(k as string)) continue;
      if ((base as unknown as Record<string, unknown>)[k as string] === draft[k]) delete draft[k];
    }
    if (Object.keys(draft).length === 0) delete values[id];
  }

  /* ------------------------------------------------------------------- add */

  function initialsOf(name: string | null): string | null {
    if (!name) return null;
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return null;
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return parts
      .map((p) => p[0])
      .join('')
      .slice(0, 4)
      .toUpperCase();
  }

  function nowHHMM(): string {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  /** 'HH:MM' passes through; an ISO-8601 timestamp renders as local HH:MM. */
  function timeValue(v: string | null): string {
    if (!v) return '';
    if (/^\d{2}:\d{2}$/.test(v)) return v;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  const TEST_POINT_OPTIONS = (Object.keys(CHUTE_TEST_POINT_LABELS) as ChuteTestPoint[]).map(
    (value) => ({ value, label: CHUTE_TEST_POINT_LABELS[value], tone: 'neutral' as const })
  );

  async function addTest() {
    if (readonly || adding) return;
    adding = true;
    addError = null;
    try {
      // `unknown` because the prop's return type includes `void`, which TS
      // refuses to let you test for truthiness directly.
      const created: unknown = await onCreate?.({
        // First row of a pour is the initial/start-up test; every later one
        // defaults to an interval test, which is what the procedure asks for
        // every 3 m3. Both are one tap to change.
        test_point: chuteTests.length === 0 ? 'initial' : 'interval',
        sample_time: nowHHMM(),
        tester_initials: initialsOf(shiftName)
      });
      if (created && typeof created === 'object' && 'id' in created) {
        expanded[(created as ChuteTest).id] = true;
      }
    } catch (e) {
      addError = e instanceof Error ? e.message : 'Could not add a test. Tap to retry.';
    } finally {
      adding = false;
    }
  }

  /* ---------------------------------------------------------------- delete */

  function askDelete(id: number) {
    if (confirmTimer) clearTimeout(confirmTimer);
    confirmingDelete = id;
    // Inline, self-cancelling. No dialog can trap a user mid-pour.
    confirmTimer = setTimeout(() => (confirmingDelete = null), 4000);
  }

  async function doDelete(id: number) {
    if (confirmTimer) clearTimeout(confirmTimer);
    confirmingDelete = null;
    saving[id] = true;
    errors[id] = null;
    try {
      await onDelete?.(id);
      delete values[id];
      delete dirty[id];
      delete expanded[id];
    } catch (e) {
      errors[id] = e instanceof Error ? e.message : 'Could not delete.';
    } finally {
      saving[id] = false;
    }
  }

  /* -------------------------------------------------------------- computed */

  function wcOf(t: ChuteTest) {
    return wcRatioDisplay(t.water_vol_l, t.grout_wt_kg);
  }
  function wcExact(t: ChuteTest) {
    return wcRatio(t.water_vol_l, t.grout_wt_kg);
  }
  function spreadOf(t: ChuteTest) {
    return spreadVerdict(t.spread_in);
  }
  function flowOf(t: ChuteTest) {
    return flowVerdict(t.flow_seconds);
  }
  function rangeFlag(v: string | null): 'ok' | 'warn' | null {
    if (v === null) return null;
    return v === 'in_range' ? 'ok' : 'warn';
  }
</script>

<section class="chute" aria-label="Chute tests">
  <header class="head">
    <h2>Chute tests</h2>
    <span class="count">{rows.length}</span>
    {#if !readonly}
      <button type="button" class="add" onclick={addTest} disabled={adding}>
        {adding ? 'Adding…' : '+ Add test'}
      </button>
    {/if}
  </header>

  {#if addError}
    <p class="banner bad" role="alert">
      {addError}
      <button type="button" class="retry" onclick={addTest}>Retry</button>
    </p>
  {/if}

  {#if rows.length === 0}
    <p class="empty">
      No chute tests recorded yet.{readonly ? '' : ' Add one when the first sample is taken.'}
    </p>
  {/if}

  <ol class="rows">
    {#each rows as { base, view } (base.id)}
      {@const wc = wcOf(view)}
      {@const sv = spreadOf(view)}
      {@const fv = flowOf(view)}
      {@const open = expanded[base.id] ?? false}
      <li class="card" class:unsaved={isDirty(base.id)}>
        <!-- Summary: what you scan for without opening the row. -->
        <div class="summary">
          <button
            type="button"
            class="toggle"
            aria-expanded={open}
            onclick={() => (expanded[base.id] = !open)}
          >
            <span class="seq">#{base.seq}</span>
            <span class="point">{CHUTE_TEST_POINT_LABELS[view.test_point]}</span>
            <span class="time">{timeValue(view.sample_time) || '--:--'}</span>
            <span class="chev" aria-hidden="true">{open ? '▾' : '▸'}</span>
          </button>

          <div class="badges">
            <VerdictBadge
              compact
              tone={wc === null ? 'muted' : 'info'}
              label="W/C"
              detail={wc === null ? '—' : wc.toFixed(2)}
            />
            <VerdictBadge
              compact
              tone={sv === null ? 'muted' : sv === 'in_range' ? 'pass' : 'warn'}
              label="Spread"
              detail={view.spread_in === null ? '—' : `${view.spread_in} in`}
            />
            <VerdictBadge
              compact
              tone={fv === null ? 'muted' : fv === 'in_range' ? 'pass' : 'warn'}
              label="Flow"
              detail={view.flow_seconds === null ? '—' : `${view.flow_seconds}s`}
            />
            <VerdictBadge
              compact
              tone={view.accepted === null ? 'muted' : view.accepted === 1 ? 'pass' : 'fail'}
              label="Acc"
              detail={view.accepted === null ? '—' : view.accepted === 1 ? 'Yes' : 'No'}
            />
          </div>

          <div class="state" aria-live="polite">
            {#if saving[base.id]}
              <span class="pill saving">Saving…</span>
            {:else if isDirty(base.id)}
              <span class="pill dirty">Unsaved</span>
              <button type="button" class="retry" onclick={() => flush(base.id)}>Save</button>
            {/if}
          </div>
        </div>

        {#if errors[base.id]}
          <p class="banner bad" role="alert">
            {errors[base.id]}
            <button type="button" class="retry" onclick={() => flush(base.id)}>Retry</button>
          </p>
        {/if}

        {#if open}
          <div class="body">
            <!-- Sample -->
            <div class="group">
              <YesNoField
                label="Test point"
                value={view.test_point}
                options={TEST_POINT_OPTIONS}
                clearable={false}
                disabled={readonly}
                compact
                fill={false}
                onValue={(v) => {
                  if (v) {
                    set(base.id, 'test_point', v);
                    flush(base.id);
                  }
                }}
              />
              <div class="grid">
                <NumberField
                  label="Approx volume"
                  unit="m³"
                  value={view.approx_volume_m3}
                  disabled={readonly}
                  compact
                  onValue={(v) => set(base.id, 'approx_volume_m3', v)}
                  onCommit={() => flush(base.id)}
                />
                <InitialsTimeField
                  label="Sample time / tester"
                  initials={view.tester_initials}
                  time={view.sample_time}
                  {shiftName}
                  disabled={readonly}
                  compact
                  timeLabel="Sample time"
                  onInitials={(v) => set(base.id, 'tester_initials', v)}
                  onTime={(v) => set(base.id, 'sample_time', v)}
                  onCommit={() => flush(base.id)}
                />
              </div>
            </div>

            <!-- Acceptance readings -->
            <div class="group">
              <h3>Acceptance readings</h3>
              <div class="grid">
                <NumberField
                  label="Spread"
                  unit="in"
                  value={view.spread_in}
                  hint="Target {SPREAD_MIN_IN}–{SPREAD_MAX_IN} in"
                  flag={rangeFlag(sv)}
                  disabled={readonly}
                  compact
                  onValue={(v) => set(base.id, 'spread_in', v)}
                  onCommit={() => flush(base.id)}
                />
                <NumberField
                  label="Flow"
                  unit="s"
                  value={view.flow_seconds}
                  hint="Target {FLOW_MIN_S}–{FLOW_MAX_S} s"
                  flag={rangeFlag(fv)}
                  disabled={readonly}
                  compact
                  onValue={(v) => set(base.id, 'flow_seconds', v)}
                  onCommit={() => flush(base.id)}
                />
              </div>
              {#if sv && sv !== 'in_range'}
                <p class="advice">{SPREAD_VERDICT_LABELS[sv]}</p>
              {/if}
              {#if fv && fv !== 'in_range'}
                <p class="advice">{FLOW_VERDICT_LABELS[fv]}</p>
              {/if}
              <NumberField
                label="Mud balance"
                unit="lb/ft³"
                value={view.mud_balance}
                hint="Recorded only — no acceptance range has been supplied"
                disabled={readonly}
                compact
                onValue={(v) => set(base.id, 'mud_balance', v)}
                onCommit={() => flush(base.id)}
              />
            </div>

            <!-- Batch numbers: these three produce the W/C ratio. -->
            <div class="group">
              <h3>Batch</h3>
              <div class="grid three">
                <NumberField
                  label="Counts"
                  integer
                  value={view.counts}
                  disabled={readonly}
                  compact
                  onValue={(v) => set(base.id, 'counts', v)}
                  onCommit={() => flush(base.id)}
                />
                <NumberField
                  label="Grout weight"
                  unit="kg"
                  value={view.grout_wt_kg}
                  disabled={readonly}
                  compact
                  onValue={(v) => set(base.id, 'grout_wt_kg', v)}
                  onCommit={() => flush(base.id)}
                />
                <NumberField
                  label="Water volume"
                  unit="L"
                  value={view.water_vol_l}
                  disabled={readonly}
                  compact
                  onValue={(v) => set(base.id, 'water_vol_l', v)}
                  onCommit={() => flush(base.id)}
                />
              </div>
              <div class="wc" aria-live="polite">
                <VerdictBadge
                  tone={wc === null ? 'muted' : 'info'}
                  label="W/C ratio"
                  detail={wc === null ? 'enter water and grout weight' : wc.toFixed(2)}
                />
                {#if wcExact(view) !== null}
                  <span class="wc-exact">exact {wcExact(view)?.toFixed(4)}</span>
                {/if}
              </div>
            </div>

            <!-- Temperatures and identification -->
            <div class="group">
              <h3>Temperatures &amp; identification</h3>
              <div class="grid">
                <NumberField
                  label="Grout temp"
                  unit="°C"
                  value={view.grout_temp_c}
                  disabled={readonly}
                  compact
                  onValue={(v) => set(base.id, 'grout_temp_c', v)}
                  onCommit={() => flush(base.id)}
                />
                <NumberField
                  label="Water temp"
                  unit="°C"
                  value={view.water_temp_c}
                  disabled={readonly}
                  compact
                  onValue={(v) => set(base.id, 'water_temp_c', v)}
                  onCommit={() => flush(base.id)}
                />
              </div>
              <div class="grid three">
                <label class="text">
                  <span>Pile</span>
                  <input
                    type="text"
                    autocomplete="off"
                    disabled={readonly}
                    value={view.pile ?? ''}
                    oninput={(e) =>
                      set(base.id, 'pile', e.currentTarget.value === '' ? null : e.currentTarget.value)}
                    onblur={() => flush(base.id)}
                  />
                </label>
                <label class="text">
                  <span>Mix no</span>
                  <input
                    type="text"
                    autocomplete="off"
                    disabled={readonly}
                    value={view.mix_no ?? ''}
                    oninput={(e) =>
                      set(base.id, 'mix_no', e.currentTarget.value === '' ? null : e.currentTarget.value)}
                    onblur={() => flush(base.id)}
                  />
                </label>
                <NumberField
                  label="Strength"
                  unit="MPa"
                  value={view.strength_mpa}
                  disabled={readonly}
                  compact
                  onValue={(v) => set(base.id, 'strength_mpa', v)}
                  onCommit={() => flush(base.id)}
                />
              </div>
            </div>

            <!-- Result -->
            <div class="group">
              <YesNoField
                label="Accepted"
                value={view.accepted as SqlBool | null}
                disabled={readonly}
                onValue={(v) => {
                  set(base.id, 'accepted', v as SqlBool | null);
                  flush(base.id);
                }}
              />
              <NotesField
                value={view.notes}
                collapsible
                disabled={readonly}
                onValue={(v) => set(base.id, 'notes', v)}
                onCommit={() => flush(base.id)}
              />
              <PhotoField
                {pourId}
                section="chute_test"
                rowId={base.id}
                {photos}
                label="Photos for test #{base.seq}"
                actionLabel="📷 Add photo"
                caption="Chute test #{base.seq}"
                readOnly={photosReadOnly}
                {closedAt}
              />
            </div>

            {#if !readonly}
              <div class="rowactions">
                {#if confirmingDelete === base.id}
                  <button type="button" class="danger" onclick={() => doDelete(base.id)}>
                    Confirm delete #{base.seq}
                  </button>
                  <button type="button" class="quiet" onclick={() => (confirmingDelete = null)}>
                    Keep
                  </button>
                {:else}
                  <button type="button" class="quiet" onclick={() => askDelete(base.id)}>
                    Delete test
                  </button>
                {/if}
              </div>
            {/if}
          </div>
        {/if}
      </li>
    {/each}
  </ol>

  {#if !readonly && rows.length > 0}
    <button type="button" class="add wide" onclick={addTest} disabled={adding}>
      {adding ? 'Adding…' : '+ Add test'}
    </button>
  {/if}
</section>

<style>
  .chute {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  h2 {
    margin: 0;
    font-size: 1.0625rem;
    font-weight: 800;
    letter-spacing: 0.01em;
  }

  h3 {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--fg-muted, #3f4750);
  }

  .count {
    font-size: 0.8125rem;
    font-weight: 800;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    background: var(--surface-muted, #eef1f4);
    color: var(--fg-muted, #3f4750);
  }

  .add {
    margin-left: auto;
    min-height: 3rem;
    padding: 0 1rem;
    font-size: 1rem;
    font-weight: 800;
    border: 2px solid var(--accent, #0b63c5);
    border-radius: 0.5rem;
    background: var(--accent, #0b63c5);
    color: #fff;
    cursor: pointer;
    touch-action: manipulation;
  }

  .add.wide {
    margin-left: 0;
    width: 100%;
  }

  .add:disabled {
    opacity: 0.6;
  }

  .empty {
    margin: 0;
    padding: 1rem;
    border: 2px dashed var(--border, #9aa3ad);
    border-radius: 0.5rem;
    color: var(--fg-muted, #3f4750);
    font-size: 0.9375rem;
  }

  .rows {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
  }

  .card {
    border: 2px solid var(--border, #9aa3ad);
    border-radius: 0.625rem;
    background: var(--surface, #fff);
    overflow: hidden;
  }

  .card.unsaved {
    border-color: var(--warn, #a35a00);
  }

  .summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
  }

  .toggle {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    flex: 1 1 12rem;
    min-height: 3rem;
    padding: 0.25rem;
    border: 0;
    background: transparent;
    font: inherit;
    text-align: left;
    color: var(--fg, #10151b);
    cursor: pointer;
    touch-action: manipulation;
  }

  .toggle:focus-visible {
    outline: 3px solid var(--accent, #0b63c5);
    outline-offset: 2px;
  }

  .seq {
    font-weight: 900;
    font-variant-numeric: tabular-nums;
  }

  .point {
    font-weight: 700;
    flex: 1 1 auto;
    min-width: 0;
  }

  .time {
    font-variant-numeric: tabular-nums;
    color: var(--fg-muted, #3f4750);
    font-weight: 700;
  }

  .chev {
    font-size: 1rem;
    color: var(--fg-muted, #3f4750);
  }

  .badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
    flex: 1 1 100%;
  }

  .state {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .pill {
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.15rem 0.45rem;
    border-radius: 999px;
  }

  .pill.dirty {
    background: var(--warn-bg, #fff6e6);
    color: var(--warn, #8a4b00);
  }

  .pill.saving {
    background: var(--surface-muted, #eef1f4);
    color: var(--fg-muted, #3f4750);
  }

  .banner {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0 0.5rem 0.5rem;
    padding: 0.5rem 0.625rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .banner.bad {
    background: var(--bad-bg, #fdecea);
    color: var(--bad, #b3261e);
  }

  .retry {
    min-height: 2.5rem;
    padding: 0 0.75rem;
    font: inherit;
    font-weight: 800;
    border: 2px solid currentColor;
    border-radius: 0.4rem;
    background: transparent;
    color: inherit;
    cursor: pointer;
    touch-action: manipulation;
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
    padding: 0.75rem 0.5rem 0.875rem;
    border-top: 2px solid var(--border-soft, #dde2e7);
  }

  .group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(8.5rem, 1fr));
    gap: 0.5rem;
  }

  .grid.three {
    grid-template-columns: repeat(auto-fit, minmax(6.5rem, 1fr));
  }

  .advice {
    margin: 0;
    padding: 0.375rem 0.5rem;
    border-left: 4px solid var(--warn, #a35a00);
    background: var(--warn-bg, #fff6e6);
    color: var(--warn, #8a4b00);
    font-size: 0.875rem;
    font-weight: 700;
  }

  .wc {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .wc-exact {
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
    color: var(--fg-muted, #3f4750);
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  .text span {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--fg-muted, #3f4750);
  }

  .text input {
    box-sizing: border-box;
    width: 100%;
    /* 16px minimum so iOS Safari does not zoom on focus. */
    font-size: 1rem;
    font-weight: 600;
    min-height: 2.75rem;
    padding: 0.5rem;
    border: 2px solid var(--border, #9aa3ad);
    border-radius: 0.5rem;
    background: var(--surface, #fff);
    color: var(--fg, #10151b);
  }

  .text input:focus {
    outline: none;
    border-color: var(--accent, #0b63c5);
    box-shadow: 0 0 0 3px var(--accent-ring, rgba(11, 99, 197, 0.28));
  }

  .rowactions {
    display: flex;
    gap: 0.5rem;
    padding-top: 0.25rem;
  }

  .quiet,
  .danger {
    min-height: 2.75rem;
    padding: 0 0.875rem;
    font: inherit;
    font-weight: 700;
    border-radius: 0.5rem;
    cursor: pointer;
    touch-action: manipulation;
  }

  .quiet {
    border: 2px solid var(--border, #9aa3ad);
    background: transparent;
    color: var(--fg-muted, #3f4750);
  }

  .danger {
    border: 2px solid var(--bad, #b3261e);
    background: var(--bad, #b3261e);
    color: #fff;
  }
</style>
